import React from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { CreateDataSetVersionSchema } from 'libs/end-to-end/src/types/databases/data-sets/create-data-set-version-request-body';
import { DataSet } from 'libs/end-to-end/src/types/databases/data-sets/data-set';
import { DataSetVersion } from 'libs/end-to-end/src/types/databases/data-sets/data-set-version';
import { DataSetVersions } from 'libs/end-to-end/src/types/databases/data-sets/data-set-version';
import { DataSetVersionFiles } from 'libs/end-to-end/src/types/databases/data-sets/data-set-version';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { RunDataSetVersionPostProcessingBodyType } from 'libs/end-to-end/src/types/databases/data-sets/run-data-set-version-post-processing-request-body';

export interface DataSetPullUploadPageHook {
  uploadDataSetTitle: string;
  errorMessage: string | null;
  displayMode: 'pull' | 'upload';
  pullButtonBehavior: TouchTargetBehavior;
  pullButtonLabel: string;
  pullButtonDisabled: boolean;
  backButtonBehavior: TouchTargetBehavior;
  backButtonLabel: string;
  backButtonDisabled: boolean;
  uploadFileButtonBehavior: TouchTargetBehavior;
  uploadFileButtonLabel: string;
  uploadFileButtonDisabled: boolean;
  isModalOpen: boolean;
  setIsModalOpen: Setter<boolean>;
  tryCreateVersion: (args: { userUploadId: string | null }) => void;
  userId: string | null;
  versions: DataSetVersions;
  runPostProcessingForVersion: (
    dataSetVersion: string,
    shouldRunAdditionalWorkflows: boolean,
  ) => void;
  currentPage: number;
  setCurrentPage: Setter<number>;
  totalPages: number;
  canGoToPreviousPage: boolean;
  canGoToNextPage: boolean;
  showPullToast: boolean;
  setShowPullToast: Setter<boolean>;
  showSuccessToast: boolean;
  setShowSuccessToast: Setter<boolean>;
  showErrorToast: boolean;
  setShowErrorToast: Setter<boolean>;
  isLoadingVersions: boolean;
}

export const useDataSetPullUploadPage = ({
  databaseId,
  dataSetId,
  organizationId,
}: {
  organizationId: string;
  databaseId: string;
  dataSetId: string;
}): DataSetPullUploadPageHook => {
  if (!dataSetId || !databaseId || !organizationId) {
    throw new Error('Missing required parameters');
  }

  const navigate = useNavigateSafe();
  const { myUser, userAuthenticatedApiCall } = useUser();

  const userId = useMemo(() => {
    return myUser?.id ?? null;
  }, [myUser]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showPullToast, setShowPullToast] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [pollingVersionId, setPollingVersionId] = useState<string | null>(null);
  const [newVersion, setNewVersion] = useState<DataSetVersion | null>(null);
  const [newVersionFiles, setNewVersionFiles] = useState<{
    versionFiles: Record<string, DataSetVersionFiles>;
    totalNumberOfVersionFiles: Record<string, number>;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageLimit = 8;

  const dataSetReadPatternGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}`;
  }, [dataSetId, databaseId, organizationId]);

  const dataSetReadPattern = useSimpleReadPattern({
    getUrlFn: dataSetReadPatternGetUrlFn,
    responseSchema: DataSet,
    canRead: true,
  });

  const dataSetVersionsReadPatternGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}/versions?page=${currentPage}&limit=${pageLimit}`;
  }, [dataSetId, databaseId, organizationId, currentPage]);

  const dataSetVersionsReadPattern = useSimpleReadPattern({
    getUrlFn: dataSetVersionsReadPatternGetUrlFn,
    responseSchema: DataSetVersions,
    canRead: true,
  });

  useEffect(() => {
    if (
      dataSetVersionsReadPattern.status === 'done' ||
      dataSetVersionsReadPattern.status === 'error'
    ) {
      dataSetVersionsReadPattern.reloadEntity();
    }
  }, [currentPage]);

  const versionWriteGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}/versions`;
  }, [dataSetId, databaseId, organizationId]);

  const versionWritePattern = useSimpleWritePattern({
    getUrlFn: versionWriteGetUrlFn,
    responseSchema: DataSetVersion,
    canWrite: true,
    method: 'POST',
    writeSchema: CreateDataSetVersionSchema,
  });

  const tryCreateVersion = useCallback(
    ({ userUploadId }: { userUploadId: string | null }) => {
      if (versionWritePattern.status === 'saving') {
        return;
      }

      versionWritePattern.saveEntity({
        userUploadId,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        status: 'NotStarted',
      });
    },
    [versionWritePattern],
  );

  const runPostProcessingForVersion = useAsyncCallback(
    async (
      isAlive: IsAliveFn,
      dataSetVersion: string,
      shouldRunAdditionalWorkflows: boolean,
    ) => {
      if (!isAlive()) {
        return;
      }

      const request: RunDataSetVersionPostProcessingBodyType = {
        shouldRunAdditionalWorkflows,
      };

      const runResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}/versions/${dataSetVersion}/post-processing`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(request),
        },
        responseSchema: EmptyObject,
      });

      if (!isAlive()) {
        return;
      }

      if (!runResult || runResult.resultType === 'err') {
        return;
      }

      navigate(
        `/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}/pull-upload`,
      );
    },
    [dataSetId, databaseId, navigate, organizationId, userAuthenticatedApiCall],
  );

  useEffect(() => {
    if (
      versionWritePattern.status === 'done' &&
      versionWritePattern.saved &&
      versionWritePattern.entity
    ) {
      setIsModalOpen(false);
      setShowPullToast(true);
      setPollingVersionId(versionWritePattern.entity.dataSetVersion);
      const newVersionEntity = {
        ...versionWritePattern.entity,
        status: 'Processing' as const,
      };
      setNewVersion(newVersionEntity);

      setNewVersionFiles({
        versionFiles: {
          [newVersionEntity.dataSetVersion]: {
            fileNames: [],
            fileConsoleUrls: {},
          },
        },
        totalNumberOfVersionFiles: {
          [newVersionEntity.dataSetVersion]: 0,
        },
      });

      if (currentPage !== 1) {
        setCurrentPage(1);
      } else if (
        dataSetVersionsReadPattern.status === 'done' ||
        dataSetVersionsReadPattern.status === 'error'
      ) {
        dataSetVersionsReadPattern.reloadEntity();
      }
    }
  }, [
    dataSetId,
    databaseId,
    navigate,
    organizationId,
    versionWritePattern,
    currentPage,
  ]);

  useEffect(() => {
    if (!pollingVersionId) {
      return;
    }

    const pollInterval = setInterval(async () => {
      const url = `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}/versions?page=1&limit=${pageLimit}`;
      const result = await userAuthenticatedApiCall({
        url,
        requestOptions: { method: 'GET' },
        responseSchema: DataSetVersions,
      });

      if (result && result.resultType === 'ok') {
        const version = result.value.versions.find(
          (v) => v.dataSetVersion === pollingVersionId,
        );

        if (version) {
          setNewVersion(version);

          setNewVersionFiles({
            versionFiles: {
              [pollingVersionId]: result.value.versionFiles[
                pollingVersionId
              ] || {
                fileNames: [],
                fileConsoleUrls: {},
              },
            },
            totalNumberOfVersionFiles: {
              [pollingVersionId]:
                result.value.totalNumberOfVersionFiles[pollingVersionId] || 0,
            },
          });

          if (version.status === 'Complete' || version.status === 'Failed') {
            setPollingVersionId(null);
            setShowPullToast(false);

            if (version.status === 'Complete') {
              setShowSuccessToast(true);
            } else {
              setShowErrorToast(true);
            }
          }
        }
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [
    pollingVersionId,
    pageLimit,
    organizationId,
    databaseId,
    dataSetId,
    userAuthenticatedApiCall,
  ]);

  const versions = useMemo(() => {
    let baseVersions;

    if (dataSetVersionsReadPattern.status === 'done') {
      baseVersions = dataSetVersionsReadPattern.entity;
    } else {
      baseVersions = {
        versions: [],
        versionFiles: {},
        totalNumberOfVersionFiles: {},
      };
    }

    if (newVersion && currentPage === 1) {
      const existingVersionIndex = baseVersions.versions.findIndex(
        (v) => v.dataSetVersion === newVersion.dataSetVersion,
      );

      let updatedVersions;
      let updatedVersionFiles = { ...baseVersions.versionFiles };
      let updatedTotalNumberOfVersionFiles = {
        ...baseVersions.totalNumberOfVersionFiles,
      };

      if (newVersionFiles) {
        updatedVersionFiles = {
          ...updatedVersionFiles,
          ...newVersionFiles.versionFiles,
        };
        updatedTotalNumberOfVersionFiles = {
          ...updatedTotalNumberOfVersionFiles,
          ...newVersionFiles.totalNumberOfVersionFiles,
        };
      }

      if (existingVersionIndex >= 0) {
        updatedVersions = [...baseVersions.versions];
        updatedVersions[existingVersionIndex] = newVersion;
      } else {
        updatedVersions = [newVersion, ...baseVersions.versions];
      }

      if (!updatedVersionFiles[newVersion.dataSetVersion]) {
        updatedVersionFiles[newVersion.dataSetVersion] = {
          fileNames: [],
          fileConsoleUrls: {},
        };
      }
      if (
        updatedTotalNumberOfVersionFiles[newVersion.dataSetVersion] ===
        undefined
      ) {
        updatedTotalNumberOfVersionFiles[newVersion.dataSetVersion] = 0;
      }

      return {
        ...baseVersions,
        versions: updatedVersions,
        versionFiles: updatedVersionFiles,
        totalNumberOfVersionFiles: updatedTotalNumberOfVersionFiles,
      };
    }

    return baseVersions;
  }, [currentPage, dataSetVersionsReadPattern, newVersion, newVersionFiles]);

  const totalPages = versions.pagination?.totalPages ?? 0;
  const canGoToPreviousPage = currentPage > 1;
  const canGoToNextPage = currentPage < totalPages;

  return useMemo(() => {
    return {
      uploadDataSetTitle: 'Upload a new version of the data set',
      displayMode:
        dataSetReadPattern.status === 'done' &&
        (dataSetReadPattern.entity.config.dataSetType === 'Integration' ||
          dataSetReadPattern.entity.config.dataSetType === 'Composite')
          ? 'pull'
          : 'upload',
      errorMessage:
        versionWritePattern.status === 'error'
          ? JSON.stringify(versionWritePattern.error)
          : null,
      pullButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          tryCreateVersion({ userUploadId: null });
        },
      },
      pullButtonLabel: 'Pull Data Set',
      pullButtonDisabled:
        dataSetReadPattern.status !== 'done' ||
        versionWritePattern.status === 'saving',
      backButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          navigate(
            `/organizations/${organizationId}/databases/${databaseId}/data-sets`,
          );
        },
      },
      backButtonLabel: 'Back',
      backButtonDisabled:
        dataSetReadPattern.status !== 'done' ||
        versionWritePattern.status === 'saving',
      uploadFileButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          setIsModalOpen(true);
        },
      },
      uploadFileButtonLabel: 'Upload File to Data Set',
      uploadFileButtonDisabled:
        dataSetReadPattern.status !== 'done' ||
        versionWritePattern.status === 'saving',
      isModalOpen: isModalOpen,
      setIsModalOpen: setIsModalOpen,
      tryCreateVersion: tryCreateVersion,
      userId: userId,
      versions,
      runPostProcessingForVersion,
      currentPage,
      setCurrentPage,
      totalPages,
      canGoToPreviousPage,
      canGoToNextPage,
      showPullToast,
      setShowPullToast,
      showSuccessToast,
      setShowSuccessToast,
      showErrorToast,
      setShowErrorToast,
      isLoadingVersions: dataSetVersionsReadPattern.status === 'loading',
    };
  }, [
    dataSetReadPattern,
    versionWritePattern,
    userId,
    isModalOpen,
    tryCreateVersion,
    navigate,
    organizationId,
    databaseId,
    runPostProcessingForVersion,
    versions,
    currentPage,
    totalPages,
    canGoToPreviousPage,
    canGoToNextPage,
    showPullToast,
    showSuccessToast,
    showErrorToast,
  ]);
};
